import { CollapsibleSection } from "@components/shared/CollapsibleSection";
import { RegenerateConfirmDialog } from "@components/shared/RegenerateConfirmDialog";
import {
  GENERATE_CANDIDATE_EMAIL_MESSAGES,
  GENERATE_CANDIDATE_LINKEDIN_CONNECTION_REQUESTS,
  GENERATE_CANDIDATE_LINKEDIN_MESSAGES,
} from "@lib/graphql/mutations";
import { CAMPAIGN_OUTREACH_WORKSPACE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { AlertTriangle, FileText, Inbox, Linkedin, type Mail } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { OutreachDisclaimer } from "./OutreachDisclaimer";
import { OutreachDraftEditor } from "./OutreachDraftEditor";
import { type ListScope, OutreachDraftList } from "./OutreachDraftList";
import {
  CHANNELS,
  channelSpec,
  type DraftKind,
  draftFor,
  draftState,
  type WorkspaceCandidate,
} from "./outreachDrafts";

interface CampaignData {
  id: string;
  title: string;
  brief: { id: string } | null;
  roleBlueprint: { id: string } | null;
  campaignCandidates: WorkspaceCandidate[];
}

interface GeneratePayload {
  status?: string | null;
  warning?: string | null;
  blockedCampaignCandidateIds?: string[];
  errors?: string[];
}

const NO_CONTEXT_REASON = "Generate a brief or build a role blueprint first";

/**
 * RUN › Outreach: a drafts workspace shaped like a mail client's Drafts folder
 * (#3169). Left: candidates with per-channel draft status. Right: the open
 * candidate's drafts, one tab per channel, with copy / regenerate / save and
 * "Mark as sent", which logs the contact honestly. Rarion never sends anything.
 */
export function OutreachWorkspace() {
  const { id } = useParams();
  const campaignId = id!;
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("cc");
  const channel = channelSpec(params.get("channel")).kind;
  const [scope, setScope] = useState<ListScope>("outreach");
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingOverwrite, setPendingOverwrite] = useState<{
    ids: string[];
    warning: string;
  } | null>(null);

  const [{ data, fetching, error }, reexecute] = useQuery<{ campaign: CampaignData }>({
    query: CAMPAIGN_OUTREACH_WORKSPACE_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const [, generateEmails] = useMutation(GENERATE_CANDIDATE_EMAIL_MESSAGES);
  const [, generateLinkedin] = useMutation(GENERATE_CANDIDATE_LINKEDIN_MESSAGES);
  const [, generateConnect] = useMutation(GENERATE_CANDIDATE_LINKEDIN_CONNECTION_REQUESTS);

  const campaign = data?.campaign;
  const all = useMemo(() => campaign?.campaignCandidates ?? [], [campaign]);
  const atOutreach = useMemo(() => all.filter((cc) => cc.stage === "outreach"), [all]);
  const listed = scope === "outreach" ? atOutreach : all;
  const selected = all.find((cc) => cc.id === selectedId) ?? null;

  // A deep link to a candidate who is not at the outreach stage widens the scope
  // so the row is visible.
  useEffect(() => {
    if (selected && selected.stage !== "outreach" && scope === "outreach") setScope("all");
  }, [selected, scope]);

  const hasContext = Boolean(campaign?.brief || campaign?.roleBlueprint);

  const generatingCount = useMemo(
    () =>
      listed.reduce(
        (n, cc) =>
          n + CHANNELS.filter((spec) => draftState(draftFor(cc, spec)) === "generating").length,
        0,
      ),
    [listed],
  );

  const generate = useCallback(
    async (kind: DraftKind, ids: string[], confirmOverwrite = false) => {
      if (ids.length === 0) return;
      setErrorMessage(null);
      const input: Record<string, unknown> = { campaignId, campaignCandidateIds: ids };
      let payload: GeneratePayload | undefined;
      if (kind === "email") {
        const result = await generateEmails({ input: { ...input, confirmOverwrite } });
        payload = result.data?.generateCandidateEmailMessages;
      } else if (kind === "linkedin_message") {
        const result = await generateLinkedin({ input });
        payload = result.data?.generateCandidateLinkedinMessages;
      } else {
        const result = await generateConnect({ input });
        payload = result.data?.generateCandidateLinkedinConnectionRequests;
      }
      const errors = payload?.errors ?? [];
      if (errors.length) {
        setErrorMessage(errors.join("; "));
        return;
      }
      if (payload?.status === "manual_edits_present") {
        setPendingOverwrite({ ids, warning: payload.warning ?? "" });
        return;
      }
      refresh();
    },
    [campaignId, generateEmails, generateLinkedin, generateConnect, refresh],
  );

  const toggleChecked = (ccId: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ccId)) next.delete(ccId);
      else next.add(ccId);
      return next;
    });

  const toggleAll = () =>
    setCheckedIds((prev) => {
      const listedIds = listed.map((cc) => cc.id);
      const allChecked = listedIds.every((id) => prev.has(id));
      const next = new Set(prev);
      for (const id of listedIds) {
        if (allChecked) next.delete(id);
        else next.add(id);
      }
      return next;
    });

  // Bulk draft for the ticked candidates. Candidates that already have a draft
  // of that kind are skipped (regenerate is per candidate, in the editor).
  const draftForChecked = (kind: DraftKind) => {
    const spec = channelSpec(kind);
    const ticked = listed.filter((cc) => checkedIds.has(cc.id));
    const targets = ticked.filter((cc) => draftState(draftFor(cc, spec)) === "none");
    const skipped = ticked.length - targets.length;
    if (targets.length === 0) {
      setNotice(
        `Every selected candidate already has ${spec.pluralLabel === "emails" ? "an email" : `a ${spec.label.toLowerCase()}`} draft — open one to regenerate it.`,
      );
      return;
    }
    setNotice(
      `Drafting ${spec.pluralLabel} for ${targets.length} ${targets.length === 1 ? "candidate" : "candidates"}${
        skipped > 0 ? ` (${skipped} already drafted, skipped)` : ""
      }.`,
    );
    void generate(
      kind,
      targets.map((cc) => cc.id),
    );
  };

  const select = (ccId: string, nextChannel?: DraftKind) => {
    const next = new URLSearchParams(params);
    next.set("cc", ccId);
    next.set("channel", nextChannel ?? channel);
    setParams(next, { replace: true });
  };

  if (fetching && !campaign) {
    return <div className="p-6 text-sm text-text-muted">Loading outreach drafts…</div>;
  }
  if (!campaign) {
    return (
      <div className="p-6 text-sm text-text-muted" data-testid="workspace-unavailable">
        {error ? `Could not load outreach drafts: ${error.message}` : "Campaign not found."}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Inbox size={22} className="mt-0.5 text-accent" />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-text-primary">Outreach drafts</h2>
          <p className="text-sm text-text-muted">
            Personalised emails, LinkedIn messages and connection requests, drafted from the brief,
            the role blueprint and each candidate's profile.
          </p>
          <OutreachDisclaimer className="mt-1" />
        </div>
      </div>

      {errorMessage && (
        <div
          className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="error-message"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,2fr)_3fr]">
        <OutreachDraftList
          campaignId={campaignId}
          candidates={listed}
          outreachCount={atOutreach.length}
          totalCount={all.length}
          scope={scope}
          onScopeChange={setScope}
          selectedId={selectedId}
          onSelect={(ccId) => select(ccId)}
          checkedIds={checkedIds}
          onToggleChecked={toggleChecked}
          onToggleAll={toggleAll}
          generatingCount={generatingCount}
          canDraft={hasContext}
          cannotDraftReason={hasContext ? null : NO_CONTEXT_REASON}
          onDraft={draftForChecked}
          notice={notice}
        />

        {selected ? (
          <OutreachDraftEditor
            candidate={selected}
            channel={channel}
            onChannelChange={(kind) => select(selected.id, kind)}
            onGenerate={(kind) => generate(kind, [selected.id])}
            onChanged={refresh}
          />
        ) : (
          <div
            data-testid="no-candidate-selected"
            className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-border p-6 text-center text-sm text-text-muted"
          >
            Select a candidate to open their drafts.
          </div>
        )}
      </div>

      <CollapsibleSection title="Job adverts" storageKey="outreach-job-adverts">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <AdvertCard
            icon={Linkedin}
            title="LinkedIn job posting"
            description="A single LinkedIn-formatted job posting drafted for the whole campaign."
            href={`/campaigns/${campaignId}/linkedin-job-posting`}
          />
          <AdvertCard
            icon={FileText}
            title="Job posting (branded)"
            description="Branded job posting applying your organisation's template, logo and tone."
            href={`/campaigns/${campaignId}/branded-job-posting`}
          />
        </div>
      </CollapsibleSection>

      <RegenerateConfirmDialog
        open={pendingOverwrite !== null}
        warning={pendingOverwrite?.warning ?? ""}
        onCancel={() => setPendingOverwrite(null)}
        onConfirm={() => {
          const ids = pendingOverwrite?.ids ?? [];
          setPendingOverwrite(null);
          void generate("email", ids, true);
        }}
      />
    </div>
  );
}

function AdvertCard({
  icon: Icon,
  title,
  description,
  href,
}: {
  icon: typeof Mail;
  title: string;
  description: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="block rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring transition hover:ring-2 hover:ring-accent/30"
    >
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-accent" />
        <h4 className="text-sm font-medium text-text-primary">{title}</h4>
      </div>
      <p className="mt-1 text-xs text-text-secondary">{description}</p>
    </Link>
  );
}
